import {
  Backdrop,
  Button, CircularProgress, Grid, List, ListSubheader,
  Paper, Skeleton, Stack, Table, TableBody, TableContainer,
  TableHead, TableRow, Typography
} from '@mui/material';
import React, { useState, useEffect, Fragment, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import toastr from 'toastr';
import { isEqual } from "lodash";
import {
  FormHeaderPaper, FormTitleTypography, FormTableCell,
  FormTableRow, DataEntryTextField, SectorTitleTypography, TableListItem, FormDescriptionTypography
} from '../../../components/CustomStyles/StyledComponents';
import _ from 'lodash';
import produce from 'immer';
import { ScrollSync, ScrollSyncPane } from 'react-scroll-sync';
import { toastrCustomOptions, toastrCustomOptionsLonger } from '../../../constants/toastrOptions';
import {
  getCropUseFormData,
  getCropUseMetaData,
  resetCropUseMetaData,
  resetResponseState,
  saveCropUseData,
  setCropUseFormData,
  setLoading,
  unsetLoading
} from '../../../store/appAction';
import { useNavigate } from 'react-router-dom';
import DataEntryButtonStack from '../../../components/Buttons/DataEntryButtonStack';
import { addCommas, removeCommas } from '../../../helpers/stringFormatHelper';
import { getFirstVisibleCellDetailsInDataEntry } from '../../../helpers/dataEntryHelper';
import SelectedFarm from '../../../components/SelectedFarm/SelectedFarm';
import SelectedReport from '../../../components/SelectedReport/SelectedReport';
import store from '../../../store';

const CELL_WIDTH = {
  RowTitle: "18%",
  BeefCropUsePercent: "12%",
  SheepCropUsePercent: "12%",
  DairyCropUsePercent: "12%",
  PigCropUsePercent: "12%",
  PoultryCropUsePercent: "12%",
  Total: "12%",
};

const LAND_AREA_TOTAL_KEY = 'TotalLandArea';
const CROP_USE_TOTAL_KEY = 'CropUseLivestcockTotal';

const DataEntryTable = ({ sector, initialFormData, firstVisibleCellDetails, locked }) => {
  toastr.options = toastrCustomOptions;
  const dispatch = useDispatch();
  const [sectorDataInt, setSectorDataInt] = useState(undefined);
  const [sectorDataExt, setSectorDataExt] = useState(undefined);

  useEffect(() => {
    setSectorDataInt(initialFormData);
    setSectorDataExt(initialFormData);
  }, [initialFormData])

  useEffect(() => {
    if (sectorDataExt && !_.isEmpty(sectorDataExt)) {
      dispatch(setCropUseFormData({
        sectorId: sector.id,
        formData: sectorDataExt
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sectorDataExt]);

  const handleInputChange = useCallback((e, groupId, rowId, rowData, mappingProperty, rule) => {
    let value = removeCommas(e.target.value);

    if (e.target.inputMode === 'numeric') {
      let numericRegex = /^[0-9]*(\.[0-9]{0,2})?$/;
      if (numericRegex.test(value)) {
        let numericValue = parseFloat(value || 0);
        if (rule.min > numericValue || rule.max < numericValue) {
          toastr.warning(`This input only accepts values between ${rule.min} and ${addCommas(rule.max)}. Please enter the value again.`,
            undefined,
            toastrCustomOptionsLonger
          );
        } else {
          setSectorDataInt(produce((draft) => {
            draft[groupId][rowId][mappingProperty] = value;
            let tempRowData = { ...draft[groupId][rowId] };
            delete tempRowData[CROP_USE_TOTAL_KEY];
            delete tempRowData[LAND_AREA_TOTAL_KEY];
            let rowValues = Object.values(tempRowData);
            const totalValue = rowValues.reduce((x, y) => parseFloat(x || 0) + parseFloat(y || 0))
            draft[groupId][rowId][CROP_USE_TOTAL_KEY] = totalValue || '';
          }));
          setSectorDataExt(produce((draft) => {
            draft[groupId][rowId][mappingProperty] = parseFloat(value);
            let tempRowData = { ...draft[groupId][rowId] };
            delete tempRowData[CROP_USE_TOTAL_KEY];
            delete tempRowData[LAND_AREA_TOTAL_KEY];
            let rowValues = Object.values(tempRowData);
            const totalValue = rowValues.reduce((x, y) => parseFloat(x || 0) + parseFloat(y || 0))
            draft[groupId][rowId][CROP_USE_TOTAL_KEY] = parseFloat(totalValue);
          }));
        }
      }
    }
  }, []);

  const getCellContent = ({ groupId, rowId, cell, rowData, firstVisibleCellDetails, locked }) => {
    switch (cell.dataType) {
      case 'TextField':
        return (
          <DataEntryTextField
            key={rowId + cell.mappingProperty}
            size="small"
            variant="outlined"
            inputProps={{ inputMode: 'numeric' }}
            style={{
              width: 100, textAlign: 'center',
              caretColor: cell.mappingProperty === CROP_USE_TOTAL_KEY ? "transparent" : "",
            }}
            value={(rowData && addCommas(rowData[cell.mappingProperty])) || ''}
            error={cell.mappingProperty === CROP_USE_TOTAL_KEY &&
              (parseFloat((rowData && rowData[cell.mappingProperty]) || 0) > 100)}
            onChange={(e) => {
              cell.mappingProperty !== CROP_USE_TOTAL_KEY &&
                handleInputChange(e, groupId, rowId, rowData,
                  cell.mappingProperty, cell.validations)
            }}
            autoFocus={ locked === false && rowId === firstVisibleCellDetails.rowId && cell.id === firstVisibleCellDetails.cellId }
          />
        );
      case 'Empty':
      default:
        return <></>;
    }
  }

  const fetchRowTitle = (title, totalArea) => {
    const totalInKm = ['Hedges']; //temporarily check by title
    return title.concat(
      ' (', addCommas(totalArea || 0),
      ' ', totalInKm.includes(sector.title) ? 'km' : 'ha', ')');
  }

  return (
    <>
      {sector ?
        <ScrollSyncPane>
          <TableContainer component={Paper}>
            <Table sx={{ minWidth: 700 }} size="small" aria-label="customized table">
              <TableBody>
                {(sector.sectorGroups.groups || []).map((group) => (
                  <Fragment>
                    {group.rows.map((row) => (
                      <FormTableRow>
                        <FormTableCell sx={{ width: CELL_WIDTH['RowTitle'] }} >
                          {sectorDataInt &&
                            sectorDataInt[group.id] &&
                            sectorDataInt[group.id][row.id] &&
                            fetchRowTitle(row.title, sectorDataInt[group.id] &&
                              sectorDataInt[group.id][row.id][LAND_AREA_TOTAL_KEY])}
                        </FormTableCell>
                        {row.cells.map((cell) => (
                          <FormTableCell sx={{ width: /*CELL_WIDTH[cell.mappingProperty] */'12%' }}>
                            {sectorDataInt && sectorDataInt[group.id] && cell.visible &&
                              getCellContent({
                                groupId: group.id,
                                rowId: row.id,
                                cell: cell,
                                rowData: sectorDataInt[group.id] && sectorDataInt[group.id][row.id],
                                firstVisibleCellDetails: firstVisibleCellDetails,
                                locked: locked
                              })}
                          </FormTableCell>
                        ))}
                      </FormTableRow>
                    ))}
                  </Fragment>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </ScrollSyncPane>
        : <Skeleton variant="rectangular" width={'100%'} height={150} />
      }
    </>
  );
}


const CropUse = (props) => {
  const dispatch = useDispatch();
  let navigate = useNavigate();

  const reportId = useSelector(state => state.common.reportId);
  const metaData = useSelector(state => state.cropUse.metaData);
  const initialFormData = useSelector(state => state.cropUse.initialFormData);
  const { successMsg, errorMsg, loading } = useSelector(state => state.common);
  const farmId = useSelector(state => state.common.farmId);
  const adminFarm = useSelector(state => state.adminFarm);
  const {reportDetails: { reportName, includeBiochar },
         reportConfig: { landAndCropsOnly, livestockOnly }} = useSelector(state => state.farmReport);

  const [formData, setFormData] = useState({})
  const [modalOpen, setModalOpen] = useState(false);
  const [nextClicked, setNextClicked] = useState(false);
  const [firstVisibleCellDetails, setFirstVisibleCellDetails] = useState(null);

  const sectors = metaData?.form?.sectors || [];
  const columns = metaData?.form?.columns || [];

  useEffect(() => {
    dispatch(setLoading());
    dispatch(getCropUseMetaData({ reportId }));
    dispatch(getCropUseFormData({ reportId }));
    dispatch(resetResponseState());
    // eslint-disable-next-line react-hooks/exhaustive-deps
    return () => {
      dispatch(resetCropUseMetaData());
    }
  }, []);

  useEffect(() => {
    if (initialFormData && !_.isEmpty(initialFormData)) {
      const tempData = { ...initialFormData };
      dispatch(unsetLoading());
      setFormData(tempData);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialFormData]);

  const navigateNext = () =>{
    // helper to be used in 2 locations
    if (includeBiochar) {
      navigate('/data-entry/land-crops/biochar');
    } else if (landAndCropsOnly) {
      navigate('/data-entry/energy-waste/electricity-fuel');
    } else {
      //Crop use allocated to livestock only appears if there is livestock
      navigate('/data-entry/livestock/numbers-weights');
    }
  }

  useEffect(() => {
    if (successMsg || errorMsg) {
      dispatch(unsetLoading());
      successMsg && toastr.success(successMsg);
      errorMsg && toastr.error(errorMsg, undefined, toastrCustomOptionsLonger);
      dispatch(getCropUseFormData({ reportId }));
      dispatch(resetResponseState());
      if (successMsg && nextClicked) {
        navigateNext();
      }
      setNextClicked(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [successMsg, errorMsg]);

  useEffect(() => {
    const firstVisibleCellDetails = getFirstVisibleCellDetailsInDataEntry(sectors);
    setFirstVisibleCellDetails(firstVisibleCellDetails);
  }, [sectors]);

  const handleModalResponse = (positiveResponse) => {
    setModalOpen(false);
    if (positiveResponse) {
      // window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
     window.scrollY = 0;
      dispatch(getCropUseMetaData({ reportId }));
      dispatch(getCropUseFormData({ reportId }));
    }
  }

  const isFormUpdated = () => {
    const cropUseData = store.getState().cropUse;
    const isUpdated = !isEqual(cropUseData?.initialFormData, cropUseData?.currentFormData);
    return isUpdated;
  }

  const handleSaveClick = (e) => {
    if(!isFormUpdated()){
      return;
    }
    dispatch(setLoading());
    dispatch(saveCropUseData({ reportId }));
  }

  const handleNextClick = (e) => {
    setNextClicked(true);
    if (props.locked || !isFormUpdated()) {
      // Read only, no saving data 
      navigateNext();
    } else {
      // first we save
      dispatch(setLoading());
      dispatch(saveCropUseData({ reportId }));
    };
  }

  return (
    <ScrollSync>
      <Grid container spacing={2} className={props.locked && 'deactivated'}>
        <Grid item xs={12} md={12} lg={12}>
          <FormHeaderPaper
            sx={{
              p: 2,
              display: 'flex',
              flexDirection: 'column',
            }}
            divider={false}
          >
            <SelectedFarm farmId={farmId} farmName={adminFarm.farmDetails.farmName}/>
            <SelectedReport reportName={reportName} />
            <FormTitleTypography variant="h5" component="div">
              Crop use allocated to livestock
            </FormTitleTypography>
            <FormDescriptionTypography variant="body2" paragraph>
              If home-produced crops are used for livestock grazing, feed, or bedding, allocate crops and grazing across livestock enterprises.
            </FormDescriptionTypography>
          </FormHeaderPaper>
        </Grid>
        <Grid item xs={12} sx={{ paddingTop:'0 !important' }}>
          <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column', paddingTop:'0' }}>
            <List sx={{ mb: 2 }}>
              <ListSubheader sx={{ bgcolor: "background.paper" }}>
                <ScrollSyncPane>
                  <>
                  <DataEntryButtonStack
                      modalOpen={modalOpen}
                      setModalOpen={setModalOpen}
                      handleModalResponse={handleModalResponse}
                      handleSaveClick={handleSaveClick}
                      handleNextClick={handleNextClick} />
                  <TableContainer component={Paper}>
                    <Table sx={{ minWidth: 700 }} size="small" aria-label="customized table">
                      <TableHead>
                        <TableRow>
                          <FormTableCell sx={{ width: CELL_WIDTH['RowTitle'] }}></FormTableCell>
                          {columns.map((column) =>
                            <FormTableCell key={column.id+column.title} align="center" sx={{ width: /*CELL_WIDTH[column.cssClass]*/'12%' }} >
                              {column.title}
                            </FormTableCell>
                          )}
                        </TableRow>
                      </TableHead>
                    </Table>
                  </TableContainer>
                  </>
                </ScrollSyncPane>
              </ListSubheader>
              {sectors.map((sector, index) => (
                <React.Fragment key={sector.id}>
                  <TableListItem>
                    <SectorTitleTypography variant='body1'>{sector.title}</SectorTitleTypography>
                  </TableListItem>
                  <TableListItem>
                    <DataEntryTable
                      sectorId={sector.id}
                      sector={sector}
                      initialFormData={formData[sector.id] ? formData[sector.id] : {}}
                      firstVisibleCellDetails={firstVisibleCellDetails}
                      locked={props.locked}
                    />
                  </TableListItem>
                </React.Fragment>
              ))}
            </List>
          </Paper>
        </Grid>
      </Grid>
    </ScrollSync>
  );
}

export default CropUse;